import { Dispatch, SetStateAction } from 'react';
import { QueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import Fuse from 'fuse.js';

import { ALLOWED_REMOTE_VIEWER_ROLES } from '../../AppConstants';
import ErrorHandler from '../../components/ErrorHandler';
import { TOAST_TYPE_MAP, ToastCustom } from '../../components/ToastCustom';
import { canUserMarkDamaged, userHasRoleAccess } from '../../helpers/AccessManagementHelpers';
import {
    getDevicesReleasedDueToCompletingModalContent,
    getLoggersFailedOnComplete
} from '../../helpers/ModalContentHelpers';
import { downloadScanDocument } from '../../helpers/ScanningHelpers';
import { getUserSettings } from '../../helpers/UserHelpers';
import { getBoxQueryKey } from '../../services/hooks/useGetBox';
import { getScanQueryKey } from '../../services/hooks/useGetScan';
import { GroupType, ScanType } from '../../services/scan-types/schemas';
import { ImportInfo } from '../../types/Box';
import { LoggerId, LoggerListItem, ScanOrBoxSubmitErrorRes } from '../../types/Logger';
import { GenericModalData } from '../../types/Modal';
import { ImportScanPayload, ScanBoxCommon, ScanOrBoxSubmitRes, ScanRes } from '../../types/Scan';
import { User } from '../../types/User';

export const validateLoggers = (
    scanItem: ScanBoxCommon,
    areFailedLoggersAllowed: boolean
): { successLoggers: Array<LoggerListItem>; failedLoggers: Array<LoggerListItem> } => {
    const loggersToValidate: Array<LoggerListItem> = scanItem.loggers;
    if (areFailedLoggersAllowed) {
        return {
            successLoggers: loggersToValidate,
            failedLoggers: []
        };
    }

    const successLoggers: Array<LoggerListItem> = [];
    const failedLoggers: Array<LoggerListItem> = [];

    for (const logger of loggersToValidate) {
        if (logger.masterStatus === 'failed') {
            failedLoggers.push(logger);
        } else {
            successLoggers.push(logger);
        }
    }

    return { successLoggers, failedLoggers };
};

export const isMarkAsDamagedEnabled = (scan: ScanBoxCommon | undefined, currentUser: User) => {
    if (!currentUser) return false;

    return Boolean(
        canUserMarkDamaged(currentUser) &&
            scan &&
            [GroupType.AD_HOC_BOX, GroupType.INBOUND].includes(scan.scanTypeGroupType)
    );
};

export const isFailedLoggerAllowedForScanType = (
    scanItem: ScanBoxCommon | undefined,
    scanTypes: Array<ScanType> | undefined
) => {
    if (!scanItem || !scanTypes) return false;

    return scanTypes.some((type) => type.allowFailedChecks && type.name === scanItem.scanTypeName);
};

const getScanPath = (scanId: number, boxId?: number) => {
    const suffix = boxId ? `/box-scan/${scanId}` : '';
    return `/scanning${suffix}`;
};

export const handleValidateAndSubmitSuccess = (
    submitRes: ScanOrBoxSubmitRes,
    setModalData: Dispatch<SetStateAction<GenericModalData | null>>,
    scanItemRes: ScanBoxCommon | undefined,
    navigate: (path: string) => void
) => {
    if (submitRes.success && scanItemRes) {
        const scanId = submitRes.scanId;
        const boxId = submitRes.boxId;
        if (!boxId) {
            const settings = getUserSettings();
            if (settings?.automaticallyDownloadXlsOnScanCompletion) {
                downloadScanDocument('excel', 'xlsx', scanItemRes);
            }
        }

        const releasedShipments = submitRes.actions?.releasedShipments;
        const successfullyReleased = releasedShipments?.successfullyReleased ?? [];
        const failedToRelease = releasedShipments?.failedToRelease ?? [];

        const scanPath = getScanPath(scanId, boxId);

        if (successfullyReleased.length || failedToRelease.length) {
            setModalData({
                ...getDevicesReleasedDueToCompletingModalContent(successfullyReleased, failedToRelease, Boolean(boxId)),
                closeMe: () => {
                    navigate(scanPath);
                }
            });
        } else {
            ToastCustom(`${boxId ? 'Box' : 'Scan'} ${boxId ?? scanItemRes?.id} completed`, TOAST_TYPE_MAP.SUCCESS);
            navigate(scanPath);
        }
    } else {
        /* Some loggers have failed checks after they were added to the scan. Submit invalidates tag for
        getScan, getScan hook runs again, and loggers that failed are displayed at the top of the list */
        const loggerListContainingErrLoggers: Record<string, any> = submitRes.loggers;
        const failedLoggerIds: Array<LoggerId> = [];

        if (loggerListContainingErrLoggers && Object.keys(loggerListContainingErrLoggers).length > 0) {
            for (const value of Object.values(loggerListContainingErrLoggers)) {
                if (value.masterStatus === 'failed') {
                    failedLoggerIds.push(value.loggerId);
                }
            }
        }
        setModalData({
            ...getLoggersFailedOnComplete(failedLoggerIds),
            closeMe: () => setModalData(null)
        });
    }
};

export const handleValidateAndSubmitError = (
    validateAndSubmitErrRes: AxiosError,
    setDevicesUsedInShipmentModalQueue: Dispatch<SetStateAction<Array<{ loggerIds: Array<LoggerId> }>>>
) => {
    const error = validateAndSubmitErrRes?.response?.data as ScanOrBoxSubmitErrorRes | undefined;
    if (error?.details?.errorCode === 'DEVICES_USED_IN_SHIPMENT') {
        const loggerIds = error.details.data?.loggerIds as Array<LoggerId>;
        setDevicesUsedInShipmentModalQueue((prevState) => [...prevState, { loggerIds: loggerIds }]);
    } else {
        ErrorHandler(validateAndSubmitErrRes);
    }
};

export const getFilteredLoggers = (searchByLoggerId: string, loggerList: Array<LoggerListItem>) =>
    searchByLoggerId
        ? new Fuse(loggerList, { keys: ['loggerId'], threshold: 0 })
              .search(searchByLoggerId)
              .map((result) => result.item)
        : loggerList;

export const validateAndConstructImportPayload = (
    importInfo: ImportInfo,
    isForceEnabled: boolean | undefined
): ImportScanPayload => {
    const loggerIdToVerify = importInfo.loggerIdToVerify;
    const sourceId = importInfo.idToImport;

    return {
        sourceType: importInfo.typeToImport,
        sourceId: sourceId.trim(),
        loggerIdToVerify: loggerIdToVerify.trim(),
        ...(isForceEnabled !== undefined && { skip_mismatch_check: isForceEnabled })
    };
};

export const isAnotherUserScanning = (scanItem: ScanRes | ScanBoxCommon | undefined, currentUser: User) =>
    scanItem?.createdByUsername !== currentUser.username;

export const doesScanBelongToUserLocation = (scanItem: ScanRes | ScanBoxCommon | undefined, currentUser: User) =>
    scanItem?.locationName === currentUser.locationName;

export const isUserRemoteViewer = (currentUser: User) => userHasRoleAccess(currentUser, ALLOWED_REMOTE_VIEWER_ROLES);

export const invalidateGetScanOrBoxQuery = (
    queryClient: QueryClient,
    scanId: string | undefined,
    boxId: string | undefined
) => {
    if (scanId) {
        queryClient.invalidateQueries({ queryKey: getScanQueryKey(scanId) });
    }

    if (boxId) {
        queryClient.invalidateQueries({ queryKey: getBoxQueryKey(boxId) });
    }
};
